/**
 * FilterBar — the search/filter card at the top of a list screen.
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ [primary fields .........................]  [מסננים נוספים (2)]  │
 *   │ (advanced fields, shown when expanded)                           │
 *   │                                           [נקה]  [🔍 חפש]        │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * The bar is layout only — each field is a controlled input owned by the screen,
 * so the screen's search logic stays exactly where it was.
 *
 * Props:
 *   primary        node   always-visible fields (wrap each in <FilterField>)
 *   advanced       node   fields behind the "more filters" toggle (optional)
 *   advancedCount  number how many advanced filters are active (shown on the toggle,
 *                         and the section opens automatically when > 0 on mount)
 *   onSearch       fn     search button (and Enter inside a FilterField input)
 *   onClear        fn     clear link — resets the screen's filter state
 *   searching      bool   spinner on the search button
 *
 * <FilterField label="סטטוס" wide>…input…</FilterField>
 *   label  string  small label above the control
 *   wide   bool    takes two grid columns (e.g. the free-text search)
 */
import React, { useState } from 'react';
import { Button } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import styles from './FilterBar.css';

export const FilterField = ({ label, wide, children }) => (
  // A <div>, not a <label>: wrapping Ant Selects/DatePickers in <label> re-fires their clicks.
  <div className={[styles.field, wide ? styles.wide : ''].join(' ')}>
    <span className={styles.label}>{label}</span>
    {children}
  </div>
);

const FilterBar = ({ primary, advanced, advancedCount = 0, onSearch, onClear, searching }) => {
  const [showAdvanced, setShowAdvanced] = useState(advancedCount > 0);

  // Enter anywhere inside the bar runs the search (except inside dropdown search boxes,
  // where Enter selects an option — those stop propagation themselves).
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT' && !e.target.closest('.ant-select')) onSearch();
  };

  return (
    <div className={styles.bar} onKeyDown={onKeyDown}>
      <div className={styles.grid}>{primary}</div>
      {advanced && showAdvanced ? <div className={`${styles.grid} ${styles.advanced}`}>{advanced}</div> : null}
      <div className={styles.footer}>
        {advanced ? (
          <Button type="text" icon={<FilterOutlined />} onClick={() => setShowAdvanced(!showAdvanced)}>
            {showAdvanced ? 'פחות מסננים' : 'מסננים נוספים'}
            {advancedCount > 0 ? <span className={styles.count}>{advancedCount}</span> : null}
          </Button>
        ) : (
          <span />
        )}
        <div className={styles.buttons}>
          {onClear ? (
            <Button type="text" onClick={onClear}>
              נקה
            </Button>
          ) : null}
          <Button type="primary" icon={<SearchOutlined />} onClick={onSearch} loading={searching}>
            חפש
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
